/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useEffect, useRef, useState } from "react";

import { useLocale } from "@superweb/intl";

import { useAction, useQuery } from "@fleet/api";

import { useLogAction } from "#internal/analytics/logActions";
import {
  searchParamTypes as t,
  useNavigate,
  useSearchParams,
} from "#internal/router-new";
import { NotFound, PageSpinner, useOperationIdContext } from "#internal/ui";
import { useErrorMessages, useSubmitErrorMessages } from "#internal/utils";

import { useGetSteps } from "../steps";
import { getEmployeeInfo } from "../get-employee-info";
import { MultipleFlowContent } from "./multiple-flow";
import { SingleFlowContent } from "./single-flow";

export const RegisterForm = ({ isIframe }: { isIframe: boolean }) => {
  const locale = useLocale();
  const navigate = useNavigate();

  const logActionSubmit = useLogAction("submit");

  const [{ ref_id: refId, specification, pro_id: proId }, setSearchParams] =
    useSearchParams({
      pro_id: t.boolean,
      ref_id: t.string,
      specification: t.string,
    });

  const { sessionId } = useOperationIdContext();

  const proIdLeadSubmittedRef = useRef(false);
  const [isProLeadSubmitting, setIsProLeadSubmitting] = useState(proId);

  const { response } = useQuery({
    url: "/fleet-forms/selfreg/v1/park/info",
    method: "GET",
    headers: {
      language: locale.toString(),
      "x-session-id": sessionId,
    },
    query: { ref_id: refId ?? "" },
  });

  const parkInfo = response.status === 200 ? response.body : undefined;

  const [formErrorMessage, setFormErrorMessage] = useState<string>();
  const errorMessages = useErrorMessages();
  const submitErrorMessages = useSubmitErrorMessages(
    parkInfo?.country_code ?? "",
  );

  const {
    profession,
    employmentType,
    isMultipleProfessions,
    isMultipleEmploymentTypes,
  } = getEmployeeInfo({
    professionSettingsList: parkInfo?.profession_settings_list,
  });
  const isMultipleFlow = isMultipleProfessions || isMultipleEmploymentTypes;
  const getSteps = useGetSteps();
  const { nextStep } = getSteps({
    formRequirements:
      profession?.form_requirements ?? parkInfo?.form_requirements,
    currentStep: "register",
    isLoginWithPro: proId,
    isMultipleProfessions,
    isMultipleEmploymentTypes,
    employmentType,
  });

  const { action: createLead } = useAction({
    url: "/fleet-forms/selfreg/v1/lead",
    method: "POST",
  });
  const { action: createProfile } = useAction({
    url: "/fleet-forms/selfreg/v1/submit",
    method: "POST",
  });
  const { response: suggestResponse, isFetching: isSuggestFetching } = useQuery(
    proId && employmentType === "park_employee"
      ? {
          url: "/fleet-forms/selfreg/v1/suggest",
          method: "GET",
          headers: {
            language: locale.toString(),
          },
          query: {
            ref_id: refId ?? "",
            employment_type: employmentType!,
          },
        }
      : undefined,
  );

  const suggest =
    suggestResponse?.status === 200 ? suggestResponse.body : undefined;

  useEffect(() => {
    if (!proId || !sessionId || !refId || proIdLeadSubmittedRef.current) {
      return;
    }

    proIdLeadSubmittedRef.current = true;

    const searchParams = new URLSearchParams({
      ref_id: refId,
      pro_id: proId.toString(),
      ...(profession?.profession_id
        ? { profession_id: profession.profession_id }
        : undefined),
      ...(employmentType ? { employment_type_id: employmentType } : undefined),
      ...(sessionId ? { sessionId } : undefined),
      ...(specification ? { specification } : undefined),
    });

    if (isMultipleFlow) {
      createLead(
        {
          headers: {
            "x-submission-id": sessionId,
            language: locale.toString(),
            "content-type": "application/json",
          },
          query: {
            ref_id: refId,
            apply_auth: true,
          },
          body: {
            agree_processing_data: true,
            agree_receive_info: true,
          },
        },
        {
          onResponse: (res) => {
            setIsProLeadSubmitting(false);

            if (res.status === 200) {
              logActionSubmit("submit_form", {
                params: "auth",
              });

              searchParams.set("lead_id", res.body.id);
              navigate(`/${nextStep ?? "download-app"}?${searchParams}`);
              return;
            }
            res;
            setFormErrorMessage(
              res.body?.code
                ? submitErrorMessages[res.body.code] ?? errorMessages.default
                : errorMessages.default,
            );
          },
        },
      );
    } else {
      if (employmentType !== "park_employee") {
        navigate(`/${nextStep}?${searchParams}`);
        return;
      }
      const hasAllSuggestFields =
        suggest?.phone && suggest.name?.first && suggest.name.last;
      if (hasAllSuggestFields) {
        createProfile(
          {
            headers: {
              "x-submission-id": sessionId,
              language: locale.toString(),
              "content-type": "application/json",
            },
            query: {
              ref_id: refId,
            },
            body: {
              personal: {
                first_name: suggest.name?.first!,
                last_name: suggest.name?.last!,
                phone: suggest.phone!,
                agree_processing_data: true,
                agree_receive_info: true,
                profession_id: profession?.profession_id,
                employment_type: employmentType,
              },
            },
          },
          {
            onResponse: (res) => {
              setIsProLeadSubmitting(false);
              if (res.status === 204) {
                navigate(`/${nextStep}?${searchParams}`);
              } else {
                setFormErrorMessage(
                  submitErrorMessages[res.body.code] ?? errorMessages.default,
                );
              }
            },
          },
        );
      } else {
        setIsProLeadSubmitting(false);
        setFormErrorMessage(errorMessages.default);
      }
    }
  }, [
    proId,
    errorMessages.default,
    locale,
    logActionSubmit,
    navigate,
    nextStep,
    refId,
    sessionId,
    submitErrorMessages,
    setSearchParams,
    specification,
    profession,
    isMultipleFlow,
    employmentType,
    createLead,
    createProfile,
    suggest,
  ]);

  if ((proId && isProLeadSubmitting) || isSuggestFetching) {
    return <PageSpinner />;
  }

  if (!refId || !sessionId || !parkInfo) {
    return <NotFound />;
  }

  if (profession?.redirect_link) {
    window.location.replace(profession.redirect_link);
    return <PageSpinner />;
  }

  const isDelivery = specification === "delivery";

  return isMultipleFlow ? (
    <MultipleFlowContent
      refId={refId}
      sessionId={sessionId}
      isDelivery={isDelivery}
      formErrorMessage={formErrorMessage}
      setFormErrorMessage={setFormErrorMessage}
      parkInfo={parkInfo}
      profession={profession}
      employmentType={employmentType}
      isMultipleProfessions={isMultipleProfessions}
      isMultipleEmploymentTypes={isMultipleEmploymentTypes}
      isIframe={isIframe}
    />
  ) : (
    <SingleFlowContent
      refId={refId}
      sessionId={sessionId}
      isDelivery={isDelivery}
      formErrorMessage={formErrorMessage}
      setFormErrorMessage={setFormErrorMessage}
      parkInfo={parkInfo}
      profession={profession}
      employmentType={employmentType}
      isIframe={isIframe}
    />
  );
};
